import { useState, useEffect } from "react";
import { Box, FormControl, styled, Autocomplete, TextField,
  IconButton, Chip } from "@mui/material";
import ExternalLaboratory from "../../../api/Admin/ExternalLaboratory";
import UnitOfMeasure from "../../../api/Admin/UnitOfMeasure";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";
import ModalSimpleButton from '../../../components/Modal/ModalSimpleButton';
import ExternalMethodForm from "./ExternalMethodForm";
import { GlobalSecondaryButton } from "../../styles";
import { Edit } from "@mui/icons-material";
import { StyledTableRow, StyledTableBody, StyledTableCell, 
  UXDataTableWithoutBody } from "../../../components/UXDataTable";
import CustomModal from "../../../components/Modal";
import MethodFacility from "../../../api/LIMS/MethodFacility";
import MethodExternalLab from "../../../api/Admin/MethodExternalLab";

const Header = styled(Box)(() => ({
  fontSize: "20px",
  marginLeft: 10,
  paddingRight: 20,
}));

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px"
}));

export const StyledTableCellSmall = styled(StyledTableCell)({
  width: 30,
});

const colHeaders = [
  { id: 'laboratoryName', label: 'Lab Name' },
  { id: 'methodName', label: 'Method Name' },
  { id: 'testFacilityAbv', label: 'Facility Abv' },
  { id: 'isActive', label: 'Active' },
  { id: 'isAccreditedTest', label: 'Accredited Test' },
  { id: 'isMETP', label: 'METP' },
  { id: 'targetTurnaroundInDays', label: 'Target Turnaround' },
  { id: 'testCostUSD', label: 'Test Cost' },
  { id: 'minimumSampleAmount', label: 'Required Amount' }
];


const ExternalMethodManagement = () => {

  const [externalLabs, setExternalLabs] = useState([]);
  const [allMethodFacilities, setAllMethodFacilities] = useState([]);
  const [methodExternalLabs, setMethodExternalLabs] = useState([]);
  const [selectedExternalLab, setSelectedExternalLab] = useState(null);
  const [selectedExternalFacilityMethod, setSelectedExternalFacilityMethod] = useState(null);
  const [externalMethodData, setExternalMethodData] = useState(null);
  const [unitOfMeasureList, setUnitOfMeasureList] = useState([]);
  const [showExternalMethodFormModal, setShowExternalMethodFormModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.AnalyticalSME, Roles.Developer]);
  const [isLoading, setIsLoading] = useState(false);

  const externalFacilityMethods = allMethodFacilities.filter(x => x.testFacilityAbv === 'EXT');

  const filteredMethodExternalLabs = methodExternalLabs.filter(x => 
    (!selectedExternalLab || x.laboratoryName === selectedExternalLab?.laboratoryName)
    && (!selectedExternalFacilityMethod || x.methodName === selectedExternalFacilityMethod?.methodName)
  ) ?? [];

  useEffect(() => {
    let cancelPromise = false;

    const fetchData = async () => {
      setIsLoading(true);

      await loadUnitOfMeasurementList();
      if (cancelPromise) return;
      await loadAllExternalLabs();
      if (cancelPromise) return;
      const allMethodFacilitiesArray = await loadAllMethodFacilities();
      if (cancelPromise) return;
      await loadMethodExternalLabs(allMethodFacilitiesArray);

      setIsLoading(false);
    };

    fetchData();

    return () => {
      cancelPromise = true;
    };
  // eslint-disable-next-line  
  }, []);


  const loadUnitOfMeasurementList = async () => {
    const res = await UnitOfMeasure.getAllActive();

    if (res.length > 0) {
      const array = res.filter(x => x.uoMName !== 'vol%' && x.uoMName !== 'wt%' 
        && (x.type === 'volume' || x.type === 'weight'));
      setUnitOfMeasureList(array.sort(
        (a, b) => a.uoMName.localeCompare(b.uoMName)));
    }
  }

  const loadAllExternalLabs = async () => {
    const res = await ExternalLaboratory.getAll();

    if (res.length > 0) {
      const array = [...res];
      setExternalLabs(array.sort(
        (a,b) => a.laboratoryName.localeCompare(b.laboratoryName)
      ));
    }
  }

  const loadAllMethodFacilities = async () => {
    const res = await MethodFacility.getAll();

    if (res.length > 0) {
      const array = res.map((mf) => {
        return {
          id: mf.id,
          methodName: mf.methodName,
          testFacilityAbv: mf.testFacilityAbv
        };
      }).sort(
        (a,b) => a.methodName.localeCompare(b.methodName)
      );

      setAllMethodFacilities(array);
      return array;
    }
    return [];
  }

  const loadMethodExternalLabs = async (allMethodFacilitiesArray) => {
    const res = await MethodExternalLab.getAll();

    if (res.length > 0) {
      const array = res.map((mel) => updateMethodExternalLab(mel, allMethodFacilitiesArray));

      setMethodExternalLabs(array.sort(
        (a,b) => a.laboratoryName.localeCompare(b.laboratoryName)
      ));
    }
  }

  const updateMethodExternalLab = (methodExternalLab, allMethodFacilitiesArray) => {
    const methodFacility = allMethodFacilitiesArray?.find(mf => mf.id === methodExternalLab.methodFacilityID);
    return {
      ...methodExternalLab,
      methodName: methodFacility?.methodName ?? '',
      testFacilityAbv: methodFacility?.testFacilityAbv ?? ''
    };
  }

  const updateMethodExternalLabInList = (updatedData) => {
    MethodExternalLab.get(updatedData.methodFacilityID, updatedData.laboratoryName).then((res) => {
      if (res) {
        const result = updateMethodExternalLab(res, allMethodFacilities);
        const updatedMethodExternalLabs = [...methodExternalLabs];
        const index = updatedMethodExternalLabs.findIndex(lab => lab.methodFacilityID === result.methodFacilityID
          && lab.laboratoryName === result.laboratoryName
        );
        if (index !== -1) {
          updatedMethodExternalLabs[index] = result;
        } else {
          updatedMethodExternalLabs.push(result);
        }
        setMethodExternalLabs(updatedMethodExternalLabs);
      }
    });
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  const openModalSimpleButton = (title, text, buttonText) => {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function determineStartArray(arrayExternalLabs) {
    if (arrayExternalLabs) {
      return stableSort(arrayExternalLabs, getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    } else {
      return []
    }
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNewExternalMethodClick = () => {
    setExternalMethodData(null);
    setShowExternalMethodFormModal(true);
  };

  const handleEditExternalMethodClick = (externalMethod) => {
    setExternalMethodData(externalMethod);
    setShowExternalMethodFormModal(true);
  };
  
  const handleExternalMethodFormCancel = () => {
    setExternalMethodData(null);
    setShowExternalMethodFormModal(false);
  }

  const handleExternalMethodFormSave = async (values, isAddExternalMethod) => {

    setIsSaving(true);

    const methodFacilityID = isAddExternalMethod ? 
      externalFacilityMethods.find(method => method.methodName === values.methodName)?.id
       : values.methodFacilityID;

    const updatedExternalMethodData = {
      methodFacilityID: methodFacilityID,
      laboratoryName: values.laboratoryName,
      isActive: values.isActive,
      isAccreditedTest: values.isAccreditedTest,
      isMETP: values.isMETP,
      testCostUSD: values.testCostUSD,
      targetTurnaroundInDays: values.targetTurnaroundInDays,
      minimumSampleAmount: values.minimumSampleAmount,
      minimumSampleAmountUoM: values.minimumSampleAmountUoM
    };

    try {
      const apiAction = isAddExternalMethod
        ? MethodExternalLab.add(updatedExternalMethodData)
        : MethodExternalLab.update(updatedExternalMethodData);

      const updatedData = await apiAction;

      updateMethodExternalLabInList(updatedData);

      openModalSimpleButton('Success', 'External Method saved successfully!', 'Ok');
      setShowExternalMethodFormModal(false);

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving External Method : ${error?.message}`, 'Ok');
    } finally {
      setIsSaving(false);
    } 
  }

  const boolChip = (value) => {
    return (<Chip
      label={value ? "Yes" : "No"}
      color={value ? "success" : undefined}
      disabled={!value}
    />);
  } 

  return (
    <StyledBox flexDirection="column" width={"1200px"} justifyContent="space-between">

      <Box>
        <FormControl sx={{ m: 1, width: "50rem", display: "flex" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading External Laboratories..."}
              id="external-labs-select"
              value={selectedExternalLab}
              onChange={(e, value) => setSelectedExternalLab(value)}
              disablePortal
              options={externalLabs  ?? []}
              getOptionLabel={(option) => option?.laboratoryName ?? ""}
              sx={{ width: "25rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="External Laboratory"/> }
              autoSelect
            />

            <Autocomplete
              noOptionsText={"Loading External Facility Methods..."}
              id="external_methods-select"
              value={selectedExternalFacilityMethod}
              onChange={(e, value) => setSelectedExternalFacilityMethod(value)}
              disablePortal
              options={externalFacilityMethods  ?? []}
              getOptionLabel={(option) => option?.methodName ?? ""}
              sx={{ width: "20rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="External Facility Method"/> }
              autoSelect
            />

          </StyledBox>
        </FormControl>
      </Box>

      <Box sx={{ display: "inline-flex", marginBottom: "30px", marginTop: "20px", alignItems: "center" }}>
        <Header>External Methods ({filteredMethodExternalLabs.length})</Header>
        <GlobalSecondaryButton
          variant="contained"
          type="button"
          onClick={() => handleNewExternalMethodClick()}
          disabled={!canEdit || isLoading}>
          Assign External Method
        </GlobalSecondaryButton>
      </Box>

      <UXDataTableWithoutBody
        tableWidth='100%'
        cols={colHeaders}
        blankFirstHeader={true}
        tableId="externalMethodTable"
        rowLength={filteredMethodExternalLabs.length}
        enablePaging={true}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        noDataFoundMessage={'No external methods found'}
        enableAddIcon={false}
        addFunction={null}
        addToolTipText={null}
        enableCheckbox={false}
        enableSorteable={true}
        order={order}
        orderBy={orderBy}
        isDataLoading={isLoading}
        handleRequestSort={handleRequestSort}
      >
        <StyledTableBody key={"myTablebody"}>
          {determineStartArray(filteredMethodExternalLabs).map((externalMethod, rowIndex) => {
            return (
              <StyledTableRow
                hover
                key={`custom-row-${rowIndex}`}> 

                <StyledTableCellSmall>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <IconButton color="info" style={{ padding: 0 }}
                        disabled={!canEdit}                                              
                    >
                        <Edit fontSize="small" color="inherit"
                          onClick={() => handleEditExternalMethodClick(externalMethod)}
                        />
                    </IconButton>
                  </Box>
                </StyledTableCellSmall>

                <StyledTableCell style={{ width: '200px' }}>{externalMethod.laboratoryName}</StyledTableCell>
                <StyledTableCell style={{ width: '120px' }}>{externalMethod.methodName}</StyledTableCell>
                <StyledTableCell style={{ width: '50px' }}>{externalMethod.testFacilityAbv}</StyledTableCell>
                <StyledTableCell style={{ width: '50px' }}>
                  {boolChip(externalMethod.isActive)}
                </StyledTableCell>
                <StyledTableCell style={{ width: '50px' }}>
                  {boolChip(externalMethod.isAccreditedTest)}
                </StyledTableCell>  
                <StyledTableCell style={{ width: '50px' }}>
                  {boolChip(externalMethod.isMETP)}
                </StyledTableCell>  
                <StyledTableCell style={{ width: '70px' }}>
                  {externalMethod.targetTurnaroundInDays !== null ? `${externalMethod.targetTurnaroundInDays} days` : ''}
                </StyledTableCell>
                <StyledTableCell style={{ width: '70px' }}>
                  {externalMethod.testCostUSD !== null ? `$ ${externalMethod.testCostUSD.toFixed(2)}` : ''}
                </StyledTableCell>
                <StyledTableCell style={{ width: '70px' }}>
                  {`${externalMethod.minimumSampleAmount ?? ""} ${externalMethod.minimumSampleAmountUoM ?? ""}`.trim()}
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </StyledTableBody>
      </UXDataTableWithoutBody>

      <CustomModal
        title={externalMethodData ? "Edit External Method" : "Assign External Method"}
        open={showExternalMethodFormModal}
        setOpen={setShowExternalMethodFormModal}
        setClose={undefined}>
        <ExternalMethodForm
          externalMethodData={externalMethodData}
          externalLabs={externalLabs}
          externalFacilityMethods={externalFacilityMethods}
          unitOfMeasureList={unitOfMeasureList}
          existingMethodExternalLabs={methodExternalLabs}
          selectedExternalLab={selectedExternalLab}
          selectedExternalFacilityMethod={selectedExternalFacilityMethod}
          isSaving={isSaving}
          onSave={handleExternalMethodFormSave} 
          onCancel={handleExternalMethodFormCancel}>
        </ExternalMethodForm>        
      </CustomModal>

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>

    </StyledBox>
  );
};

export default ExternalMethodManagement;
